/**
 * AI Workflow Service
 * Handles natural language processing and workflow generation
 */

import axios from 'axios';
import config from '../config/environment';

/**
 * Extract keywords and intent from user prompt
 */
export const extractIntent = (prompt) => {
  const lowerPrompt = prompt.toLowerCase();
  
  // Common trigger keywords
  const triggerKeywords = {
    'when': ['when', 'if', 'on', 'after', 'every time'],
    'email': ['email', 'gmail', 'mail', 'message'],
    'form': ['form', 'submission', 'submit'],
    'file': ['file', 'document', 'attachment'],
    'calendar': ['calendar', 'event', 'meeting'],
    'task': ['task', 'todo', 'item'],
    'issue': ['issue', 'bug', 'ticket'],
    'webhook': ['webhook', 'api', 'request'],
  };

  // Common action keywords
  const actionKeywords = {
    'send': ['send', 'post', 'notify', 'message'],
    'create': ['create', 'add', 'make', 'new'],
    'update': ['update', 'edit', 'modify', 'change'],
    'delete': ['delete', 'remove', 'archive'],
    'save': ['save', 'store', 'backup'],
    'copy': ['copy', 'duplicate'],
    'move': ['move', 'transfer'],
  };

  // App/service keywords - more comprehensive
  const appKeywords = {
    'gmail': ['gmail', 'google mail', 'google gmail'],
    'slack': ['slack'],
    'trello': ['trello'],
    'github': ['github', 'git'],
    'google drive': ['google drive', 'drive', 'gdrive', 'g drive'],
    'dropbox': ['dropbox'],
    'twitter': ['twitter', 'tweet'],
    'facebook': ['facebook', 'fb'],
    'linkedin': ['linkedin'],
    'salesforce': ['salesforce', 'crm'],
    'hubspot': ['hubspot'],
    'zendesk': ['zendesk', 'support'],
    'shopify': ['shopify', 'store'],
    'stripe': ['stripe', 'payment'],
    'mailchimp': ['mailchimp', 'email marketing'],
    'google': ['google'], // Generic Google match
  };

  const intent = {
    trigger: null,
    action: null,
    apps: [],
    keywords: [],
  };

  // Extract apps
  Object.keys(appKeywords).forEach((app) => {
    if (appKeywords[app].some((keyword) => lowerPrompt.includes(keyword))) {
      intent.apps.push(app);
    }
  });

  // Extract trigger intent
  Object.keys(triggerKeywords).forEach((trigger) => {
    if (triggerKeywords[trigger].some((keyword) => lowerPrompt.includes(keyword))) {
      intent.trigger = trigger;
    }
  });

  // Extract action intent
  Object.keys(actionKeywords).forEach((action) => {
    if (actionKeywords[action].some((keyword) => lowerPrompt.includes(keyword))) {
      intent.action = action;
    }
  });

  // Extract general keywords
  const words = lowerPrompt.split(/\s+/);
  intent.keywords = words.filter((word) => word.length > 3);

  return intent;
};

/**
 * Match apps to user intent with improved accuracy
 */
export const matchAppsToIntent = (prompt, availableApps) => {
  const intent = extractIntent(prompt);
  const suggestions = [];

  if (!availableApps || availableApps.length === 0) {
    return suggestions;
  }

  // Helper function to score app matches
  const scoreAppMatch = (app, intentApps) => {
    const appName = (app.name || '').toLowerCase();
    const appSlug = (app.slug || '').toLowerCase();
    let score = 0;
    
    // Special handling for common apps
    const appNameNormalized = appName.replace(/google\s+/gi, ''); // Remove "google" prefix for matching
    
    intentApps.forEach((intentApp) => {
      const intentAppLower = intentApp.toLowerCase();
      
      // Exact match gets highest score
      if (appName === intentAppLower || appSlug === intentAppLower) {
        score += 100;
      }
      // Normalized exact match (e.g., "gmail" matches "google gmail")
      else if (appNameNormalized === intentAppLower) {
        score += 90;
      }
      // Starts with gets high score
      else if (appName.startsWith(intentAppLower) || appSlug.startsWith(intentAppLower)) {
        score += 50;
      }
      // Word boundary match (e.g., "gmail" in "google gmail")
      else if (new RegExp(`\\b${intentAppLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`).test(appName) || 
               new RegExp(`\\b${intentAppLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`).test(appSlug)) {
        score += 40;
      }
      // Contains gets lower score (to avoid false matches)
      else if (appName.includes(intentAppLower) || appSlug.includes(intentAppLower)) {
        score += 15;
      }
    });
    
    // Penalize apps that are clearly wrong (e.g., amazon when looking for gmail)
    if (intentApps.some(app => app.includes('gmail') || app.includes('google'))) {
      if (appName.includes('amazon') || appName.includes('aws') || appSlug.includes('amazon') || appSlug.includes('aws')) {
        score = 0; // Completely exclude Amazon/AWS when looking for Gmail/Google
      }
    }
    
    return score;
  };

  // Find matching trigger apps with scoring
  const triggerAppsWithScores = availableApps
    .filter((app) => app.trigger)
    .map((app) => ({
      app,
      score: scoreAppMatch(app, intent.apps),
    }))
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score);

  const triggerApps = triggerAppsWithScores.map((item) => item.app);

  // Find matching action apps with scoring
  const actionAppsWithScores = availableApps
    .filter((app) => app.action)
    .map((app) => ({
      app,
      score: scoreAppMatch(app, intent.apps),
    }))
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score);

  const actionApps = actionAppsWithScores.map((item) => item.app);

  // Generate workflow suggestions - prioritize best matches
  if (triggerApps.length > 0 && actionApps.length > 0) {
    // Only create suggestions with the top matches (not all combinations)
    // Take top trigger and top action apps
    const topTriggerApp = triggerApps[0];
    const topActionApp = actionApps[0];
    
    // Create primary suggestion with best match
    suggestions.push({
      name: `${topTriggerApp.name} → ${topActionApp.name}`,
      description: `When ${topTriggerApp.name} triggers, perform ${topActionApp.name} action`,
      triggerApp: topTriggerApp,
      actionApp: topActionApp,
      confidence: calculateConfidence(intent, topTriggerApp, topActionApp),
    });
    
    // Add a few more top combinations if they're different
    if (triggerApps.length > 1 || actionApps.length > 1) {
      const secondTrigger = triggerApps[1] || topTriggerApp;
      const secondAction = actionApps[1] || topActionApp;
      
      if (secondTrigger !== topTriggerApp || secondAction !== topActionApp) {
        suggestions.push({
          name: `${secondTrigger.name} → ${secondAction.name}`,
          description: `When ${secondTrigger.name} triggers, perform ${secondAction.name} action`,
          triggerApp: secondTrigger,
          actionApp: secondAction,
          confidence: calculateConfidence(intent, secondTrigger, secondAction),
        });
      }
    }
  } else if (triggerApps.length > 0) {
    // Only trigger found
    triggerApps.slice(0, 3).forEach((triggerApp) => {
      suggestions.push({
        name: `Start with ${triggerApp.name}`,
        description: `Use ${triggerApp.name} as your workflow trigger`,
        triggerApp: triggerApp,
        confidence: 0.7,
      });
    });
  } else if (actionApps.length > 0) {
    // Only action found
    actionApps.slice(0, 3).forEach((actionApp) => {
      suggestions.push({
        name: `Add ${actionApp.name} action`,
        description: `Add ${actionApp.name} to your workflow`,
        actionApp: actionApp,
        confidence: 0.7,
      });
    });
  }

  // Sort by confidence
  suggestions.sort((a, b) => (b.confidence || 0) - (a.confidence || 0));

  return suggestions.slice(0, 5); // Return top 5
};

/**
 * Calculate confidence score for a workflow suggestion
 */
const calculateConfidence = (intent, triggerApp, actionApp) => {
  let confidence = 0.5; // Base confidence

  // Increase confidence if app names match intent
  const triggerAppName = (triggerApp.name || '').toLowerCase();
  const actionAppName = (actionApp.name || '').toLowerCase();

  intent.apps.forEach((app) => {
    if (triggerAppName.includes(app) || actionAppName.includes(app)) {
      confidence += 0.2;
    }
  });

  // Increase confidence if keywords match
  intent.keywords.forEach((keyword) => {
    if (
      triggerAppName.includes(keyword) ||
      actionAppName.includes(keyword) ||
      (triggerApp.description || '').toLowerCase().includes(keyword) ||
      (actionApp.description || '').toLowerCase().includes(keyword)
    ) {
      confidence += 0.1;
    }
  });

  return Math.min(confidence, 1.0);
};

/**
 * Parse workflow from AI response (for future AI API integration)
 */
export const parseWorkflowFromAI = async (prompt, apps, currentWorkflow = null, workflowId = null) => {
  try {
    // Minimal payload - Lambda will fetch availableApps from API
    // Only send essential workflow state if modifying existing workflow
    const payload = {
      prompt,
      workflowId,
    };
    
    // Only include currentWorkflow if it exists and we're modifying
    if (currentWorkflow) {
      payload.currentWorkflow = {
        nodes: (currentWorkflow.nodes || []).map(node => ({
          id: node.id,
          type: node.type,
          data: {
            app: node.data?.app,
            triggerKey: node.data?.triggerKey,
            actionKey: node.data?.actionKey,
          }
        })),
        edges: (currentWorkflow.edges || []).map(edge => ({
          id: edge.id,
          source: edge.source,
          target: edge.target,
        })),
      };
    }
    
    const response = await axios.post(
      `${config.AI_SERVICE_URL}/generate-workflow`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('tok_')}`,
        },
        timeout: 180000, // 3 minutes for Bedrock + API calls
      }
    );
    
    // Generate workflow name from apps involved
    let workflowName = null;
    if (response.data.workflow?.nodes) {
      const appNames = response.data.workflow.nodes
        .map(node => {
          // Try to find app name from apps list
          const app = apps.find(a => 
            (a.slug || '').toLowerCase() === (node.app || '').toLowerCase() ||
            (a.name || '').toLowerCase() === (node.app || '').toLowerCase()
          );
          return app ? app.name : node.app;
        })
        .filter(Boolean);
      
      if (appNames.length > 0) {
        workflowName = appNames.join(' → ');
      }
    }
    
    const workflowWithName = response.data.workflow ? {
      ...response.data.workflow,
      name: workflowName || 'AI Generated Workflow',
      description: response.data.explanation || 'AI generated workflow'
    } : null;
    
    return {
      suggestions: workflowWithName ? [workflowWithName] : [],
      explanation: response.data.explanation,
      confidence: response.data.confidence,
      fieldMappings: response.data.workflow?.fieldMappings || {},
      source: response.data.source || 'ai',
    };
  } catch (error) {
    console.error('AI API error:', error);
    // Fallback to local matching
    const fallback = matchAppsToIntent(prompt, apps);
    return {
      suggestions: fallback,
      source: 'fallback',
      error: error.message,
    };
  }
};

/**
 * Convert AI suggestion to workflow nodes
 */
export const suggestionToWorkflowNodes = (suggestion) => {
  const nodes = [];
  const edges = [];

  if (suggestion.triggerApp) {
    // Find a trigger from the trigger app
    const triggerKey = suggestion.triggerApp.trigger 
      ? Object.keys(suggestion.triggerApp.trigger)[0] 
      : null;
    
    if (triggerKey) {
      nodes.push({
        type: 'trigger',
        app: suggestion.triggerApp.slug || suggestion.triggerApp.name,
        trigger: triggerKey,
        position: { x: 220, y: 20 },
      });
    }
  }

  if (suggestion.actionApp) {
    // Find an action from the action app
    const actionKey = suggestion.actionApp.action
      ? Object.keys(suggestion.actionApp.action)[0]
      : null;
    
    if (actionKey) {
      nodes.push({
        type: 'action',
        app: suggestion.actionApp.slug || suggestion.actionApp.name,
        action: actionKey,
        position: { x: 220, y: 240 },
      });

      // Create edge if we have both trigger and action
      if (nodes.length === 2) {
        edges.push({
          source: nodes[0].id,
          target: nodes[1].id,
        });
      }
    }
  }

  return { nodes, edges };
};
